import * as THREE from 'three';
import { type World } from '@dimforge/rapier3d-compat';
import EventEmitter from 'eventemitter3';
import { BallPhysics, ShotEndEvent } from '@/physics/ballPhysics';
import { BallTrail, type BallTrailOptions } from '@/objects/ballTrail';
import { CourseColliderType, CourseSurfaceProperties, CourseSurfaceType } from '@/courses/surfaces';

const FIXED_DT = 1 / 60;

export interface GolfBallEvents {
  shotEnded: (event: ShotEndEvent) => void,
  holedOut: () => void,
  landed: (velocity: number) => void
}

type BallTrailClearMode = 'start' | 'end' | 'never';

type GolfBallOptions = {
  waitTime?: number;
  setupData: Partial<OpenGolfSim.SetupData>;
  /** Clear ball trail before the shot. Default is to clear when the shot ends. */
  clearTrail?: BallTrailClearMode;
  groundMeshes: THREE.Mesh[];
  /** Passed through to the BallTrail (MR needs layer 0 + near camera fades). */
  trailOptions?: BallTrailOptions;
}

export type ShotStats = {
  apex: number;
  lateral: number;
  carry: number;
  total: number;
  roll: number;
  surface: CourseColliderType;
  startPosition?: THREE.Vector3;
  landPosition?: THREE.Vector3;
  endPosition?: THREE.Vector3;
  heightSamples: number[];
  distanceSamples: number[];
  lateralSamples: number[];
}

const createDefaultStats = (): ShotStats => ({
  apex: 0, lateral: 0, carry: 0, total: 0, roll: 0,
  surface: CourseSurfaceType.Base,
  heightSamples: [],
  distanceSamples: [],
  lateralSamples: [],
});

export class GolfBall extends EventEmitter<GolfBallEvents> {
  radius: number;
  stats: ShotStats;
  isShotActive: boolean;
  startPoint: THREE.Vector3;
  aimPoint: THREE.Vector3;
  object: THREE.Mesh;
  trail?: BallTrail;
  physics: BallPhysics;
  clearTrail: BallTrailClearMode;
  #setupData: Partial<OpenGolfSim.SetupData>;
  #waitTime: number;
  #timeout?: number;
  #scene: THREE.Object3D;
  #trailOptions?: BallTrailOptions;
  // #world: World;
  // #rapier: RapierInstance;  
  #accumulator: 0;
  #frameNum: 0;
  lastShot?: OpenGolfSim.Shot;
  ballMaterial: THREE.MeshBasicMaterial;
  groundMeshes: THREE.Mesh[];

  constructor(scene: THREE.Object3D, options: GolfBallOptions) {
    super();
    this.radius = 0.0213;
    this.stats = createDefaultStats();

    const opts = options || {};
    this.groundMeshes = options.groundMeshes;
    this.clearTrail = options.clearTrail ?? 'end';
    this.#trailOptions = options.trailOptions;
    this.#setupData = options.setupData;
    this.#waitTime = options.waitTime ?? 3000;
    this.#scene = scene;
    // this.#world = world;
    // this.#rapier = R;
    this.#accumulator = 0;
    this.#frameNum = 0;
    this.startPoint = new THREE.Vector3(0, 0, 0);
    this.aimPoint = new THREE.Vector3(0, 0, 0);
    this.isShotActive = false;

    this.ballMaterial = new THREE.MeshBasicMaterial( { color: 0xeeeeee } );

    // Create ball mesh once
    const geometry = new THREE.IcosahedronGeometry(this.radius, 5);
    this.object = new THREE.Mesh(geometry, this.ballMaterial);
    this.object.castShadow = false;
    this.object.frustumCulled = false;
    this.#scene.add(this.object);
    // The ball is never clipped by the MR world mask. It is the one thing that
    // has to stay visible when the shot leaves the window — which is most of
    // every shot. Set here rather than by each page, so it cannot be forgotten.
    this.object.userData.mrMaskExempt = true;

    // Create physics once — reused across all shots
    this.physics = new BallPhysics(this.object, this.radius, this.groundMeshes);
    this.physics.on('shotEnded', event => this._onShotEnded(event));
    this.physics.on('holedOut', () => this.emit('holedOut'));
    this.physics.on('landed', (v) => this.emit('landed', v));
    this.physics.setElevation(this.#setupData?.elevation);
  }

  reset(aimPoint: THREE.Vector3, startPoint: THREE.Vector3, holePoint?: THREE.Vector3) {
    // A reset always makes the ball ready — without this, isShotActive can
    // stay latched (e.g. after a water shot) and silently refuse new shots
    clearTimeout(this.#timeout);
    this.isShotActive = false;

    this.object.visible = true;
    this.startPoint.copy(startPoint);
    this.object.position.copy(startPoint);
    this.object.position.y += this.radius;

    if (this.clearTrail === 'end') {
      this.#resetBallTrail();
    }
    
    this.aimAt(aimPoint)
  
    this.#frameNum = 0;
    
    this.physics.reset(this.object.position, holePoint);
  }

  #resetBallTrail() {
    if (!this.object) {
      console.warn('No ball object to add trail to');
      return;
    }
    if (this.trail) {
      this.trail.reset(this.object);  // reuse existing instance
    } else {
      this.trail = new BallTrail(this.#scene, this.object, this.#trailOptions);
    }
  }

  /**
   * Pre-create the trail so its shader compiles during warm-up instead of
   * mid-flight of the first shot (with clearTrail 'start' it would otherwise
   * be constructed inside launchShot).
   */
  ensureTrail() {
    if (!this.trail) {
      this.#resetBallTrail();
    }
  }

  getPosition() {
    return this.physics.mesh.position;
  }
  
  isOnGreen(preShot = false) {
    return (preShot || this.physics?.isGrounded) && this.physics?.currentSurface?.type === 'green';
  }


  _onShotEnded(event: ShotEndEvent) {
    if (!this.stats.endPosition) {
      this.stats.endPosition = this.object?.position.clone();
    }
    if (event.surface) {
      this.stats.surface = event.surface;
    }
    // if (event.isInWater) {
    //   this.isShotActive = false;
    //   this.emit('shotEnded', event);
    //   return;
    // }
    let waitTime = event.isInWater ? 500 : this.#waitTime;
    clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      this.isShotActive = !!event.isInWater;
      this.emit('shotEnded', event);
    }, waitTime);
  }

  aimAt(aimPoint: THREE.Vector3) {
    this.aimPoint = aimPoint;
    if (!this.object) {
      console.error('No ball object created yet');
      return;
    }
    const direction = new THREE.Vector3().subVectors(aimPoint, this.object.position);
    direction.y = 0; // flatten to horizontal — we only want the yaw
    direction.normalize();
    // Extract yaw angle from direction
    const yaw = Math.atan2(direction.x, direction.z);
    // Set a clean rotation (only yaw, no pitch/roll)
    this.object.rotation.set(0, yaw, 0);  
  }

  getTrailPoints() {
    return this.trail?.points.map(point => point.toArray())
  }

  /**
   * Cut the in-flight shot short — a new real ball is already being hit
   * (rapid-fire range flow). Ends the sim immediately with the stats
   * accumulated so far and emits a single shotEnded, skipping the post-shot
   * wait so the caller can reset and launch the next shot synchronously.
   */
  abortShot() {
    if (!this.isShotActive && !this.physics.isShotActive) return;
    clearTimeout(this.#timeout);
    this.physics.isShotActive = false;
    this.isShotActive = false;
    if (!this.stats.endPosition) {
      this.stats.endPosition = this.object?.position.clone();
    }
    this.emit('shotEnded', {
      surface: this.stats.surface,
      isInWater: false,
      isHoled: false,
    });
  }

  launchShot(shot: OpenGolfSim.Shot) {
    if (this.isShotActive) {
      return;
    }
    if (this.clearTrail === 'start') {
      this.#resetBallTrail();
    }
    this.isShotActive = true;
    this.lastShot = shot;
    const isPutt = shot.verticalLaunchAngle < 1;
    this.stats = createDefaultStats();

    this.#accumulator = 0;
    console.log('Shot launched, accumulator reset');

    if (this.trail) {
      // add first point
      this.trail.addPoint();
    }
    this.stats.startPosition = this.object?.position.clone();

    if (this.physics) {
      this.physics.launchShot(shot, isPutt);
    }
  }


  update(delta: number) {
    // const frameDelta = Math.min(delta, 0.1);
    let frameDelta = Math.min(delta, 0.1);
    // When display rate closely matches physics rate, snap to prevent
    // accumulator drift that causes 0-step/2-step alternation
    if (Math.abs(frameDelta - FIXED_DT) < 0.002) {
      frameDelta = FIXED_DT;
    }

    this.#accumulator += frameDelta;

    if (this.physics) {
      // Fixed-timestep physics
      let steps = 0;
      while (this.#accumulator >= FIXED_DT) {
        this.physics.update(FIXED_DT);
        this.#accumulator -= FIXED_DT;
        steps++;
      }
      // useful for debugging physics on slower devices
      // if (steps !== 1) console.log(`Steps: ${steps}, accum: ${this.#accumulator.toFixed(5)}`);
    }
    if (this.trail) {
      this.trail.update(this.isShotActive);
    }

    if (this.isShotActive && this.object) {
      const height = this.object.position.y - this.startPoint.y;

      if (this.object.position.y > this.stats.apex) {
        this.stats.apex = this.object.position.y;
      }
      this.stats.total = this.startPoint.distanceTo(this.object.position);

      if (!this.physics?.isLanded) {
        this.stats.carry = this.stats.total;
      } else {
        this.stats.roll = this.stats.total - this.stats.carry;
      }
      this.stats.lateral = this.getLateralDistance(this.startPoint, this.aimPoint, this.object.position);

      if (this.physics?.isLanded && !this.stats.landPosition) {
        this.stats.landPosition = this.object.position.clone();
      }

      if (this.#frameNum % 4 === 0) {
        this.stats.heightSamples.push(this.object.position.y);
        this.stats.lateralSamples.push(this.stats.lateral);
        this.stats.distanceSamples.push(this.stats.total);
      }
      this.#frameNum++;
    }
  }
  
  getLateralDistance(startPoint: THREE.Vector3, aimPoint: THREE.Vector3, ballPosition: THREE.Vector3) {
    // Direction vector from start to aim (XZ plane only)
    const lineDir = new THREE.Vector2(
      aimPoint.x - startPoint.x,
      aimPoint.z - startPoint.z
    ).normalize();

    // Vector from start to ball (XZ plane)
    const toBall = new THREE.Vector2(
      ballPosition.x - startPoint.x,
      ballPosition.z - startPoint.z
    );

    // 2D cross product gives signed perpendicular distance
    // Positive = right of the line, Negative = left
    const lateralDistance = lineDir.x * toBall.y - lineDir.y * toBall.x;

    return lateralDistance;
  }

}